import type { AsyncFunc } from '@ricoh/ria.client.core/types/core/function.type';

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router';
import { APP_INFO, APP_LOCALES } from '@ricoh/ria.client.core/constants/core/app';
import i18nPlugin from '@ricoh/ria.client.core/plugins/i18n';
import createBrowserStorage from '@ricoh/ria.client.core/plugins/storage/browser';

import type { CookiePromptSetting } from '~/common/model/api/cookie-prompt';
import type { WelcomeLayoutContext } from '~/components/layout/welcome';

import { HOME_PAGE_MAPPING } from '~/common/constants/feature/preferences/profile';
import { getAllowedFeaturesFetcher } from '~/services/server/api/license';
import { getCookieSettingsFetcher, setCookieSettingsFetcher } from '~/services/server/identity/account';
import { editUserProfileFetcher, getUserProfileFetcher } from '~/services/server/identity/profile';

import type { ApplyProfileResult } from './_profile.type';

const cookieStore = createBrowserStorage<CookiePromptSetting>('local', APP_INFO.WEB.STORAGE.NAME, APP_INFO.WEB.STORAGE.STORE.COOKIE_PROMPT.SETTINGS);

/**
 * Use Sync Profile with Client.
 *
 * Just use at signin times.
 *
 * @returns {AsyncFunc} Update profile.
 */
export function useSyncProfile(): AsyncFunc {
  const { shouldChangeLanguageRef } = useOutletContext<WelcomeLayoutContext>();

  const { i18n } = useTranslation();

  return useCallback<AsyncFunc>(async () => {
    // * Change language if necessary
    if (shouldChangeLanguageRef.current && APP_LOCALES.includes(i18n.language)) {
      await editUserProfileFetcher.handler({
        language: i18n.language,
      });
      shouldChangeLanguageRef.current = false;
    }
  }, [i18n.language, shouldChangeLanguageRef]);
}

/**
 * Use Apply Profile.
 *
 * @returns {AsyncFunc} Apply profile.
 */
export function useApplyProfile(): AsyncFunc<[], ApplyProfileResult> {
  return useCallback<AsyncFunc<[], ApplyProfileResult>>(async () => {
    const profile = await getUserProfileFetcher.handler();
    await i18nPlugin.setLocale(profile.language);

    const allowedFeatures = await getAllowedFeaturesFetcher.handler();

    const cookieSettings = cookieStore.get();
    const getCookieSettings = await getCookieSettingsFetcher.handler();

    //* If not exist cookie setting account and exist cookieSettings in browser. */
    if (!getCookieSettings && cookieSettings) {
      cookieStore.set({
        ...cookieSettings,
      });
      await setCookieSettingsFetcher.handler(cookieSettings.settings);
    }

    return {
      profile,
      allowedFeatures,
      homePath: HOME_PAGE_MAPPING[profile.homePage],
    };
  }, []);
}
